import { type CalendarDate, fromDate, toCalendarDate } from "@internationalized/date";
import { SnapshotConfig, getErrorMessage, getNow, isHttpStatusError } from "@sg-protect/core";
import { type ODataSelection, getFilteredFields } from "@sg-protect/odata";
import config, { REGION_URL_SEARCH_PARAM } from "@sg-protect/runtime-config";
import { MixpanelProperty, type TrackEventProperties, sharePath } from "@sg-protect/shell";
import pluralize from "pluralize";
import type { ODataUserSearchResult } from "../../api/routes/odata/search.ts";
import type { ShareableReport } from "../../api/routes/report-shares.ts";
import type { ReportShareKind } from "../../api/types.ts";
import type { AnyScopeConfig } from "../entity-selection/scopeConfig.ts";

export const MAX_SHARE_NAME_LENGTH = 256;
export const MAX_SHARE_MESSAGE_LENGTH = 1000;
export const MAX_SHARE_RECIPIENTS = 100;

export const SHARE_MODAL_NAME = "Share Modal";
export const MANAGE_ACCESS_MODAL_NAME = "Manage Access Modal";
export const SHARED_REPORTS_TAB_LOCATION = "Shared Reports Tab";
export const SHARE_LINK_MODAL_LOCATION = "Share Link Modal";
export const REPORT_LIST_PARAM = "list";
export const SHARED_REPORTS_TAB = "shared-reports";

export const EXPIRY_PRESET_DAYS = [7, 14, 30, 60, 90] as const;
export const DEFAULT_EXPIRY_DAYS = 30;
const MAX_EXPIRY_DAYS = 90;

// "current" keeps the expiry a share already has, in the Manage access window.
export type ExpiryChoice =
    | { kind: "current" }
    | { kind: "preset"; days: number }
    | { kind: "custom"; date: CalendarDate | null };

export function getToday(timeZone: string): CalendarDate {
    return toCalendarDateIn(getNow(), timeZone);
}

export function getCustomExpiryBounds(timeZone: string) {
    const today = getToday(timeZone);

    return { minValue: today.add({ days: 1 }), maxValue: today.add({ days: MAX_EXPIRY_DAYS }) };
}

export function getPresetExpiryDate(days: number, today: CalendarDate): CalendarDate {
    return today.add({ days });
}

export function getExpiryDate(choice: ExpiryChoice, timeZone: string): CalendarDate | null {
    switch (choice.kind) {
        case "preset":
            return getPresetExpiryDate(choice.days, getToday(timeZone));
        case "custom":
            return choice.date;
        default:
            return null;
    }
}

export function toCalendarDateIn(date: string | number, timeZone: string): CalendarDate {
    return toCalendarDate(fromDate(new Date(date), timeZone));
}

export function isExpiryDateAllowed(date: CalendarDate, timeZone: string): boolean {
    const { minValue, maxValue } = getCustomExpiryBounds(timeZone);

    return date.compare(minValue) >= 0 && date.compare(maxValue) <= 0;
}

export function toEndOfDayIso(date: CalendarDate, timeZone: string): string {
    return new Date(date.add({ days: 1 }).toDate(timeZone).getTime() - 1).toISOString();
}

export function buildShareLink(shareId: string, origin: string, region: string): string {
    const url = new URL(sharePath(encodeURIComponent(shareId)), origin);
    // Recipients open shares on the shared host. Local dev has no shared host, so its origin is kept.
    url.hostname = url.hostname.replace(/^protect\./, "shared.");
    url.searchParams.set(REGION_URL_SEARCH_PARAM, region);

    return url.toString();
}

export function getShareLink(shareId: string): string {
    // A static origin in Chromatic, whose per-deploy URL would change the snapshot.
    return buildShareLink(
        shareId,
        SnapshotConfig.deterministic ? "https://example.com" : window.location.origin,
        config.region
    );
}

export function isShareDeletedError(error: unknown): boolean {
    return isHttpStatusError(error, 404);
}

export function getShareErrorMessage(error: unknown, fallback: string): string {
    if (isShareDeletedError(error)) {
        return "This share was deleted, so it can't be changed anymore.";
    }

    return isHttpStatusError(error, 409)
        ? "This share has ended, so it can't be changed anymore."
        : getErrorMessage(error, fallback);
}

export function getDeleteShareErrorMessage(error: unknown): string {
    return isHttpStatusError(error, 409)
        ? "This share just changed. Please try again."
        : getErrorMessage(error, "We couldn't delete this share. Please try again.");
}

// Mirrors the backend's recipient validation, which refuses the whole share over any one person who fails it.
export const SHARE_RECIPIENT_FILTER = "isEnabled eq 'active' and email ne null";

// The filter lets a blank email through.
export function hasEmail({ email }: Pick<ODataUserSearchResult, "email">): boolean {
    return Boolean(email?.trim());
}

// Only the people listed count: a removal the refreshed list no longer has must not delete the share for the ones left.
export function isRemovingEveryone(listedRecipientIds: string[] | null, removedRecipientIds: string[]): boolean {
    return (
        listedRecipientIds !== null &&
        removedRecipientIds.some(id => listedRecipientIds.includes(id)) &&
        listedRecipientIds.every(id => removedRecipientIds.includes(id))
    );
}

export function getChangesSummary(addedCount: number, removedCount: number): string {
    return [addedCount > 0 && `${addedCount} added`, removedCount > 0 && `${removedCount} removed`]
        .filter(Boolean)
        .join(" · ");
}

// Matches the activity log's exported Summary, which has no Oxford comma.
export function formatRecipients(recipientCount: number, recipientNames?: string[] | null): string {
    if (!recipientNames?.length) {
        return pluralize("person", recipientCount, true);
    }

    return recipientNames.length === 1
        ? recipientNames[0]
        : `${recipientNames.slice(0, -1).join(", ")} and ${recipientNames.at(-1)}`;
}

const LIVE_DATA_CHANGES: Record<ReportShareKind, string> = {
    unknown: "changes",
    pmr: "new sites and permission changes",
    userAccess: "new users and access changes",
    groupAccess: "new groups and access changes"
};

// A saved share doesn't record how its rows were selected, so Manage access leaves isManualSelection unset.
export function getLiveDataWarning(report: ReportShareKind, isManualSelection?: boolean) {
    if (report === "pmr" && isManualSelection !== undefined) {
        return isManualSelection
            ? "Be sure about who you share it with. In this report, sites won't change, but recipients will see permission changes as they happen."
            : "Be sure about who you share it with. In this report, sites may change based on your filters, and recipients will see permission changes as they happen.";
    }

    return `Be sure about who you share it with. This report shows live data, so recipients will see ${LIVE_DATA_CHANGES[report] ?? LIVE_DATA_CHANGES.unknown} as they happen.`;
}

const REPORT_KIND_LABELS: Record<ShareableReport, string> = {
    pmr: "Permissions matrix",
    userAccess: "User access",
    groupAccess: "Group access"
};

export function getReportKindLabel(reportKind?: ReportShareKind | null): string | undefined {
    return reportKind && reportKind !== "unknown" ? REPORT_KIND_LABELS[reportKind] : undefined;
}

const SHAREABLE_REPORTS: Partial<Record<string, ShareableReport>> = {
    siteCollections: "pmr",
    users: "userAccess",
    groupPrincipals: "groupAccess"
};

export function getShareableReport(selection: ODataSelection): ShareableReport | undefined {
    return SHAREABLE_REPORTS[selection.entity];
}

// Field names only: a filter value or a search can hold a site's or a person's name. The scope's own
// filters (crawl status, site types) aren't the admin's, so they're left out.
export function getSharedScopeProperties(
    selection: ODataSelection,
    scope: AnyScopeConfig,
    entityCount?: number
): TrackEventProperties {
    const report = getShareableReport(selection);
    const scopeFields = new Set([...getFilteredFields(scope.baseFilter), ...scope.hiddenFilters]);

    return {
        [MixpanelProperty.ReportKind]: report && REPORT_KIND_LABELS[report],
        [MixpanelProperty.FilteredFields]: getFilteredFields(selection.query.$filter).filter(
            field => !scopeFields.has(field)
        ),
        [MixpanelProperty.HasSearch]: Boolean(selection.query.$search),
        [MixpanelProperty.SelectionMode]: selection.includes?.length ? "Manual" : "Select All",
        [MixpanelProperty.EntityCount]: entityCount
    };
}
